import { Link } from "react-router-dom";
import { Heart, MapPin, BadgeCheck, Scale, Maximize2, BedDouble, Phone } from "lucide-react";
import { formatINR, configLabel } from "../lib/format";
import { useApp } from "../context/AppContext";

export function PropertyCard({ property: p, compact = false }) {
  const { toggleSaved, isSaved, toggleCompare, inCompare, openLead } = useApp();
  const isRent = p.listing_type === "rent";
  const priceLabel = isRent ? `${formatINR(p.price)}/mo` : formatINR(p.price);

  return (
    <article
      data-testid={`property-card-${p.slug}`}
      className="group bg-white rounded-2xl border border-pbs-line overflow-hidden pbs-shadow hover:pbs-shadow-hover transition-shadow duration-300 flex flex-col"
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        <Link to={`/property/${p.slug}`}>
          <img src={p.image} alt={p.name} loading="lazy"
            className="w-full h-full object-cover zoom-img" />
        </Link>
        <div className="absolute inset-x-0 top-0 p-3 flex items-start justify-between">
          <div className="flex flex-col gap-1.5">
            {p.verified && (
              <span className="inline-flex items-center gap-1 bg-pbs-purple text-white text-[11px] font-bold px-2 py-1 rounded-md shadow">
                <BadgeCheck size={13} /> Verified
              </span>
            )}
            {p.is_new_launch && (
              <span className="inline-flex items-center bg-pbs-orange text-white text-[11px] font-bold px-2 py-1 rounded-md shadow">New Launch</span>
            )}
          </div>
          <button
            data-testid={`save-btn-${p.slug}`}
            onClick={(e) => { e.preventDefault(); toggleSaved(p); }}
            className="w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors shadow"
          >
            <Heart size={17} className={isSaved(p.slug) ? "fill-pbs-coral text-pbs-coral" : "text-pbs-dark"} />
          </button>
        </div>
        <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
          <span className="text-white/90 text-[11px] font-semibold uppercase tracking-wide">{p.ready_to_move ? "Ready to Move" : p.possession_status}</span>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-pbs-magenta">{p.property_type}</span>
          <span className="text-[11px] font-semibold text-pbs-grey">{p.developer}</span>
        </div>
        <Link to={`/property/${p.slug}`}>
          <h3 className="mt-1.5 font-bold text-lg text-pbs-dark leading-snug line-clamp-1 hover:text-pbs-purple transition-colors">{p.name}</h3>
        </Link>
        <p className="mt-1 flex items-center gap-1 text-sm text-pbs-grey line-clamp-1">
          <MapPin size={14} className="text-pbs-purple shrink-0" /> {p.locality}, {p.city}
        </p>

        <div className="mt-3 flex items-center gap-4 text-sm text-pbs-dark/80">
          {p.bedrooms > 0 && <span className="flex items-center gap-1.5"><BedDouble size={15} className="text-pbs-grey" /> {p.bedrooms} BHK</span>}
          <span className="flex items-center gap-1.5"><Maximize2 size={14} className="text-pbs-grey" /> {p.area_sqft.toLocaleString("en-IN")} sq.ft</span>
        </div>

        <div className="mt-3 pt-3 border-t border-pbs-line flex items-end justify-between">
          <div>
            <p className="text-xl font-extrabold text-pbs-dark">{priceLabel}</p>
            <p className="text-[11px] text-pbs-grey font-mono">₹{p.price_per_sqft.toLocaleString("en-IN")}/sq.ft</p>
          </div>
          <button
            data-testid={`compare-btn-${p.slug}`}
            onClick={() => toggleCompare(p)}
            className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border transition-colors ${inCompare(p.slug) ? "bg-pbs-purple text-white border-pbs-purple" : "border-pbs-line text-pbs-grey hover:border-pbs-purple hover:text-pbs-purple"}`}
          >
            <Scale size={13} /> {inCompare(p.slug) ? "Added" : "Compare"}
          </button>
        </div>

        {!compact && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to={`/property/${p.slug}`} data-testid={`view-details-${p.slug}`}
              className="text-center text-sm font-bold text-pbs-purple border border-pbs-purple/30 rounded-lg py-2 hover:bg-pbs-purple/5 transition-colors">
              View Details
            </Link>
            <button data-testid={`enquire-btn-${p.slug}`}
              onClick={() => openLead({ property: p, type: "enquiry", title: `Enquire about ${p.name}` })}
              className="flex items-center justify-center gap-1.5 text-sm font-bold text-white rounded-lg py-2 pbs-gradient hover:opacity-95 transition-opacity">
              <Phone size={14} /> Contact
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

export default PropertyCard;
